import React, { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs';

import ALink from '~/components/features/custom-link';
import Card from '~/components/features/accordion/card';

import { useProgramAccess } from './use-program-access';
import {
  useStoreCategories,
  pickRandomCategories,
  categoryHasSubmenu,
} from '~/hooks/useStoreCategories';

const MAX_CATEGORIES = 11;

// MobileMenu (off-canvas) del hamburguesa. Dos tabs: "Menú" (Inicio, Tienda y
// los accesos según el programa) y "Categorías" (aún mockeada con el contenido
// del template). Se abre con el toggle del header (clase `mmenu-active` en body).
function MobileMenu() {
  const router = useRouter();
  const accessLinks = useProgramAccess();
  const { data: categoriesData = [] } = useStoreCategories();
  const categories = useMemo(
    () => pickRandomCategories(categoriesData, MAX_CATEGORIES),
    [categoriesData],
  );

  useEffect(() => {
    const hideMobileMenuHandler = () => {
      if (window.innerWidth > 991) {
        document.querySelector('body')?.classList.remove('mmenu-active');
      }
    };

    window.addEventListener('resize', hideMobileMenuHandler);
    return () => window.removeEventListener('resize', hideMobileMenuHandler);
  }, []);

  // Cerrar el sidebar al navegar (al hacer clic en cualquier link accionable).
  // El toggle de acordeón no navega, así que expandir no lo cierra.
  useEffect(() => {
    document.querySelector('body')?.classList.remove('mmenu-active');
  }, [router.asPath]);

  const hideMobileMenu = () => {
    document.querySelector('body')?.classList.remove('mmenu-active');
  };

  return (
    <div className="mobile-menu-wrapper">
      <div className="mobile-menu-overlay" onClick={hideMobileMenu}></div>

      <ALink className="mobile-menu-close" href="#" onClick={hideMobileMenu}>
        <i className="d-icon-times"></i>
      </ALink>

      <div className="mobile-menu-container scrollable">
        <Tabs selectedTabClassName="show" selectedTabPanelClassName="active" defaultIndex={0}>
          <div className="tab tab-nav-simple tab-nav-center tab-nav-boxed">
            <TabList className="nav nav-tabs nav-fill" role="tablist">
              <Tab className="nav-item">
                <span className="nav-link">Menú</span>
              </Tab>
              <Tab className="nav-item">
                <span className="nav-link">Categorías</span>
              </Tab>
            </TabList>
          </div>

          <div className="mobile-menu mmenu-anim tab-content">
            <TabPanel className="tab-pane">
              <ul>
                <li>
                  <ALink href="/">
                    <i className="d-icon-home"></i>Inicio
                  </ALink>
                </li>
                <li>
                  <ALink href="/shop">
                    <i className="d-icon-shoppingbag"></i>Tienda
                  </ALink>
                </li>

                {/* Accesos segun el programa (misma logica que el sub-nav). */}
                {accessLinks.map((link) =>
                  link.onClick ? (
                    <li key={link.key}>
                      <ALink href="#" onClick={link.onClick}>
                        <i className={link.icon}></i>
                        {link.label}
                      </ALink>
                    </li>
                  ) : link.external ? (
                    <li key={link.key}>
                      <a href={link.href} target="_blank" rel="noopener noreferrer">
                        <i className={link.icon}></i>
                        {link.label}
                      </a>
                    </li>
                  ) : (
                    <li key={link.key}>
                      <ALink href={link.href!}>
                        <i className={link.icon}></i>
                        {link.label}
                      </ALink>
                    </li>
                  ),
                )}
              </ul>
            </TabPanel>
            <TabPanel className="tab-pane">
              <ul>
                {categories.map((category) =>
                  categoryHasSubmenu(category) ? (
                    <li key={category.id}>
                      <Card
                        title={
                          <>
                            {category.icon && <i className={category.icon}></i>}
                            {category.name}
                          </>
                        }
                        type="mobile"
                        url={`/shop?category=${category.id}`}
                      >
                        <ul>
                          {category.children.map((subcategory) => (
                            <li key={subcategory.id}>
                              <ALink
                                href={{
                                  pathname: '/shop',
                                  query: { subcategory: subcategory.id },
                                }}
                                scroll={false}
                              >
                                {subcategory.name}
                              </ALink>
                            </li>
                          ))}
                        </ul>
                      </Card>
                    </li>
                  ) : (
                    <li key={category.id}>
                      <ALink
                        href={{
                          pathname: '/shop',
                          query: { category: category.id },
                        }}
                        scroll={false}
                      >
                        {category.icon && <i className={category.icon}></i>}
                        {category.name}
                      </ALink>
                    </li>
                  ),
                )}

                <li>
                  <ALink href="/shop">Ver todas las categorías</ALink>
                </li>
              </ul>
            </TabPanel>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

export default React.memo(MobileMenu);
